import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, IconButton, Stack, Fade, Chip, useTheme } from '@mui/material';
import useDataFetch, { usePostDataFetch } from '../../utils/useDataFetch';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';

import TreasuryBalanceTab from '../../components/treasury/TreasuryBalanceTab';
import TreasuryFinesTab from '../../components/treasury/TreasuryFinesTab';
import StrafenKatalog from '../../components/treasury/StrafenKatalog';

import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { useAuth } from '../../context/AuthContext';

const Treasury = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const { token } = useAuth(); // Für den auth Header beim Download

    const { data: profile } = useDataFetch({ url: '/users/profile' });
    const { data: membersData } = useDataFetch({ url: `/teams/${teamId}/members` });

    const { data: treasuryData, isLoading: treasuryLoading, refetch: refetchTreasury } = useDataFetch({ url: `/treasury/team/${teamId}` });
    const { data: finesData, refetch: refetchFines } = useDataFetch({ url: `/treasury/team/${teamId}/fines` });
    const { data: catalogData, refetch: refetchCatalog } = useDataFetch({ url: `/treasury/team/${teamId}/catalog` });

    const { postData: createEntry } = usePostDataFetch();
    const { postData: assignFine } = usePostDataFetch();
    const { postData: payFine } = usePostDataFetch();
    const { postData: createCatalogEntry } = usePostDataFetch();

    const [tab, setTab] = useState('kasse');
    const [showCatalogForm, setShowCatalogForm] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const [entryForm, setEntryForm] = useState({ beschreibung: '', betrag: '', typ: 'EINNAHME', datum: new Date().toISOString().split('T')[0] });
    const [fineForm, setFineForm] = useState({ katalog_id: '', user_id: '', datum: new Date().toISOString().split('T')[0] });
    const [catalogForm, setCatalogForm] = useState({ grund: '', betrag: '', typ: 'GELD', individueller_text: '' });

    const handleCreateEntry = async (e) => {
        e.preventDefault();
        await createEntry({ url: `/treasury/team/${teamId}/entry`, aBody: entryForm });
        setEntryForm({ beschreibung: '', betrag: '', typ: 'EINNAHME', datum: new Date().toISOString().split('T')[0] });
        refetchTreasury();
    };

    const handleCreateCatalog = async (e) => {
        e.preventDefault();
        await createCatalogEntry({ url: `/treasury/team/${teamId}/catalog`, aBody: catalogForm });
        setCatalogForm({ grund: '', betrag: '', typ: 'GELD', individueller_text: '' });
        setShowCatalogForm(false);
        refetchCatalog();
    };

    const handleAssignFine = async (e) => {
        e.preventDefault();
        await assignFine({ url: `/treasury/team/${teamId}/fines/assign`, aBody: fineForm });
        refetchFines();
    };

    const handlePayFine = async (fineId) => {
        await payFine({ url: `/treasury/fines/pay/${fineId}`, aBody: {} });
        refetchFines();
        refetchTreasury();
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/treasury/team/${teamId}/export`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Export fehlgeschlagen');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `kasse_team_${teamId}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Download error:', error);
            alert('Fehler beim Exportieren der Daten');
        } finally {
            setIsExporting(false);
        }
    };

    const team = profile?.teams?.find(t => t?.team_id?.toString() === teamId?.toString());
    const sekRoles = (team?.sekundaer_rolle || '').toString().split(',').map(s => s.trim()).filter(Boolean);
    const isKassenwart = sekRoles.includes('kassenwart') || profile?.user?.isAdmin || profile?.user?.role === 'super_admin';

    if (treasuryLoading) return (
        <CircularProgress />
    );

    const entries = treasuryData?.entries || [];
    const fines = finesData?.fines || [];
    const catalog = catalogData?.catalog || [];
    const members = membersData?.members || [];

    const totalBalance = entries.reduce((acc, curr) => curr.typ === 'EINNAHME' ? acc + parseFloat(curr.betrag) : acc - parseFloat(curr.betrag), 0);

    const aggregatedFines = {};
    fines.filter(f => !f.ist_bezahlt).forEach(f => {
        if (!aggregatedFines[f.user_id]) {
            aggregatedFines[f.user_id] = {
                name: `${f.first_name} ${f.last_name}`,
                geld: 0,
                kisten: 0,
                individuell: []
            };
        }
        if (f.typ === 'GELD') aggregatedFines[f.user_id].geld += parseFloat(f.betrag);
        else if (f.typ === 'KISTEN') aggregatedFines[f.user_id].kisten += parseInt(f.betrag);
        else aggregatedFines[f.user_id].individuell.push(f);
    });

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto', mb: 12 }}>

            {/* Header Section */}
            <Fade in timeout={800}>
                <Box sx={{ mb: 6 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton
                                onClick={() => navigate(-1)}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.05)',
                                    '&:hover': { bgcolor: 'primary.main', color: '#000' }
                                }}
                            >
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            {isKassenwart && (
                                <Chip
                                    icon={<VerifiedUserRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                                    label="Kassenwart Modus"
                                    size="small"
                                    sx={{
                                        bgcolor: 'primary.main',
                                        color: '#000',
                                        fontWeight: 900,
                                        fontSize: '0.65rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                />
                            )}
                        </Stack>

                        {isKassenwart && (
                            <Button
                                variant="outlined"
                                size="small"
                                disabled={isExporting}
                                onClick={handleExport}
                                startIcon={isExporting ? <CircularProgress size={16} /> : <FileDownloadRoundedIcon />}
                                sx={{
                                    borderColor: 'rgba(255,255,255,0.2)',
                                    color: 'text.primary',
                                    borderRadius: "12px",
                                    textTransform: 'none',
                                    fontWeight: 700
                                }}
                            >
                                {isExporting ? 'Exportiere...' : 'CSV Export'}
                            </Button>
                        )}
                    </Stack>
                    <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.04em' }}>
                        Mannschafts
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.04em' }}>
                        <span style={{ color: theme.palette.primary.main }}>kasse</span>
                    </Typography>
                </Box>
            </Fade>

            {/* Pill Navigation */}
            <Box
                sx={{
                    width: "100%",
                    display: 'inline-flex',
                    justifyContent: 'space-around',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    p: 0.75,
                    borderRadius: "12px",
                    mb: 5,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Button
                    startIcon={<AccountBalanceWalletRoundedIcon />}
                    onClick={() => setTab('kasse')}
                    sx={{
                        borderRadius: "12px",
                        px: 3,
                        py: 1,
                        fontWeight: 800,
                        bgcolor: tab === 'kasse' ? 'primary.main' : 'transparent',
                        color: tab === 'kasse' ? '#000' : 'text.secondary',
                        '&:hover': {
                            bgcolor: tab === 'kasse' ? 'primary.dark' : 'rgba(255,255,255,0.1)'
                        }
                    }}
                >
                    Kasse
                </Button>
                <Button
                    startIcon={<ReceiptLongRoundedIcon />}
                    onClick={() => setTab('strafen')}
                    sx={{
                        borderRadius: "12px",
                        px: 3,
                        py: 1,
                        fontWeight: 800,
                        bgcolor: tab === 'strafen' ? 'primary.main' : 'transparent',
                        color: tab === 'strafen' ? '#000' : 'text.secondary',
                        '&:hover': {
                            bgcolor: tab === 'strafen' ? 'primary.dark' : 'rgba(255,255,255,0.1)'
                        }
                    }}
                >
                    Strafen
                </Button>
                <Button
                    startIcon={<GavelRoundedIcon />}
                    onClick={() => setTab('katalog')}
                    sx={{
                        borderRadius: "12px",
                        px: 3,
                        py: 1,
                        fontWeight: 800,
                        bgcolor: tab === 'katalog' ? 'primary.main' : 'transparent',
                        color: tab === 'katalog' ? '#000' : 'text.secondary',
                        '&:hover': {
                            bgcolor: tab === 'katalog' ? 'primary.dark' : 'rgba(255,255,255,0.1)'
                        }
                    }}
                >
                    Strafenkatalog
                </Button>
            </Box>

            {/* Tab Content */}
            <Box sx={{ minHeight: '60vh' }}>
                {tab === 'kasse' ? (
                    <TreasuryBalanceTab
                        totalBalance={totalBalance}
                        isKassenwart={isKassenwart}
                        entryForm={entryForm}
                        setEntryForm={setEntryForm}
                        handleCreateEntry={handleCreateEntry}
                        entries={entries}
                    />
                ) : tab === 'strafen' ? (
                    <TreasuryFinesTab
                        isKassenwart={isKassenwart}
                        fineForm={fineForm}
                        setFineForm={setFineForm}
                        handleAssignFine={handleAssignFine}
                        members={members}
                        catalog={catalog}
                        showCatalogForm={showCatalogForm}
                        setShowCatalogForm={setShowCatalogForm}
                        handleCreateCatalog={handleCreateCatalog}
                        catalogForm={catalogForm}
                        setCatalogForm={setCatalogForm}
                        aggregatedFines={aggregatedFines}
                        fines={fines}
                        handlePayFine={handlePayFine}
                    />
                ) : (
                    <StrafenKatalog catalog={catalog} />
                )}
            </Box>
        </Box>
    );
};

export default Treasury;
